/**
 * @file seo-settings-store.ts
 * @description Последние известные общие SEO-настройки магазина на вкладку
 * (phase19.16, партия Р3в, v0.19.64; SB145, SB132): красная плашка «Сайт
 * закрыт от поисковиков» во всём кабинете и образец «Так в поиске» в
 * настройках страницы конструктора (окончание заголовков и описание сайта
 * теперь не в черновике вида сайта).
 *
 * Каркас магазина спрашивает `GET /admin/site/seo` один раз при входе;
 * страница «Сайт → SEO» кладёт сюда каждый новый ответ (сохранение,
 * «Вернуть прежнее») — плашка появляется и исчезает сразу, без опроса.
 * Значение помнится для кабинета вкладки: смена магазина спрашивает заново.
 */

import { useEffect, useSyncExternalStore } from "react";

import { shopSeo, type ShopSeoSettingsDto } from "../api-site-seo";
import { getTabContextId } from "../tab-context";

interface State {
  /** Кабинет вкладки, для которого значение. */
  key: string;
  settings: ShopSeoSettingsDto;
}

let state: State | null = null;
let loading: string | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const contextKey = (): string => getTabContextId() ?? "";

/** Новый ответ сервера о настройках SEO. */
export function setSeoSettingsSnapshot(settings: ShopSeoSettingsDto): void {
  state = { key: contextKey(), settings };
  emit();
}

/** Сбросить (тесты). */
export function resetSeoSettingsSnapshot(): void {
  state = null;
  loading = null;
  emit();
}

function snapshot(): ShopSeoSettingsDto | null {
  return state !== null && state.key === contextKey() ? state.settings : null;
}

/** Спросить сервер один раз на кабинет вкладки; без права — тихо, значения нет. */
function ensureLoaded(): void {
  const key = contextKey();
  if ((state && state.key === key) || loading === key) return;
  loading = key;
  shopSeo
    .get()
    .then((settings) => {
      if (contextKey() === key) setSeoSettingsSnapshot(settings);
    })
    .catch(() => {
      // Нет права или сети — плашки нет; страница SEO обновит значение сама.
    })
    .finally(() => {
      if (loading === key) loading = null;
    });
}

/** Настройки SEO магазина или null (не загружены, нет права). */
export function useShopSeoSnapshot(enabled: boolean): ShopSeoSettingsDto | null {
  useEffect(() => {
    if (enabled) ensureLoaded();
  }, [enabled]);
  const value = useSyncExternalStore(subscribe, snapshot, snapshot);
  return enabled ? value : null;
}

/** Закрыт ли сайт от поисковиков; `enabled` — есть право на сайт. */
export function useSearchIndexingClosed(enabled: boolean): boolean {
  return useShopSeoSnapshot(enabled)?.allowIndexing === false;
}
